import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'wouter';
import {
  newsTopicLabel,
  PLAN_CATALOG,
  type NewsEdition,
  type NewsEditionSource,
  type NewsEditionStory,
} from '@secretario/shared';
import { getNewsEdition, NEWS_EDITION_KEY, sendNewsSignal } from '../../../lib/api/newsEdition.js';
import { openPaywall } from '../../../lib/plan.js';
import { updateSpecialistConfig } from '../../../lib/api/team.js';
import { AI_CONSENT_EVENT } from '../../assistant/AiConsentSheet.js';
import { Skeleton } from '../../ui/Skeleton.js';
import './news-edition.css';

const editionDay = (iso: string) => {
  const text = new Date(iso).toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/** Título da seção da edição, igual em todos os estados. */
function EditionHead() {
  return (
    <h2 className="pgs-h2">
      Edição completa <small className="ne-pro">{PLAN_CATALOG.pro.name}</small>
    </h2>
  );
}

/** Folha de jornal pequena, para as observações da edição. */
function PaperIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        d="M5 5h11v14H7a2 2 0 0 1-2-2V5Zm11 4h3v8a2 2 0 0 1-2 2M8 9h5M8 12h5M8 15h3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Sources({
  story,
  sources,
}: {
  story: NewsEditionStory | null;
  sources: NewsEditionSource[];
}) {
  return (
    <div className="ne-sources" aria-label="Leia no veículo">
      {sources.map((source) => (
        <a
          key={source.url}
          className="ne-source"
          href={source.url}
          target="_blank"
          rel="noreferrer"
          onClick={() => {
            if (story) {
              void sendNewsSignal({ storyId: story.id, theme: story.theme, signal: 'open' }).catch(
                () => undefined,
              );
            }
          }}
        >
          {source.name} <i aria-hidden="true">↗</i>
        </a>
      ))}
    </div>
  );
}

function MoreOfThis({ story }: { story: NewsEditionStory }) {
  const [done, setDone] = useState(false);
  if (!story.theme) return null;
  return (
    <button
      type="button"
      className={`pgs-chip ne-chip${done ? ' on' : ''}`}
      aria-pressed={done}
      disabled={done}
      onClick={() => {
        setDone(true);
        void sendNewsSignal({ storyId: story.id, theme: story.theme, signal: 'like' }).catch(() =>
          setDone(false),
        );
      }}
    >
      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
        {done ? (
          <path
            d="m5 12.5 4.5 4.5L19 7.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M12 5v14M5 12h14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
        )}
      </svg>
      {done ? 'Anotado' : 'Mais disso'}
    </button>
  );
}

function Paragraphs({ text }: { text: string }) {
  return (
    <>
      {text.split('\n\n').map((paragraph, index) => (
        <p key={index}>{paragraph}</p>
      ))}
    </>
  );
}

function Why({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <div className="ne-why">
      <b>Por que importa pra você</b>
      <p>{text}</p>
    </div>
  );
}

function Story({ story, lead = false }: { story: NewsEditionStory; lead?: boolean }) {
  // A matéria de abertura já vem inteira; as outras mostram o começo.
  const [open, setOpen] = useState(lead);
  const topic = [lead ? 'Abre a edição' : null, story.theme ? newsTopicLabel(story.theme) : null]
    .filter(Boolean)
    .join(' · ');
  return (
    <article className="ne-story">
      {topic && <small className="ne-topic">{topic}</small>}
      <h3 className="ne-title">{story.title}</h3>
      <div className={`ne-text${open ? ' open' : ''}`}>
        <Paragraphs text={story.text} />
      </div>
      {!open && (
        <button type="button" className="ne-link" onClick={() => setOpen(true)}>
          Ler tudo
        </button>
      )}
      <Why text={story.why} />
      <div className="ne-foot">
        <Sources story={story} sources={story.sources} />
        <MoreOfThis story={story} />
      </div>
    </article>
  );
}

/** Liga ou desliga a edição completa (ela usa cidade, trabalho e assuntos da pessoa). */
function useEditionSwitch() {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  return {
    busy,
    async set(on: boolean) {
      setBusy(true);
      try {
        await updateSpecialistConfig('noticias', { editionOptIn: on });
        await queryClient.invalidateQueries({ queryKey: NEWS_EDITION_KEY });
      } finally {
        setBusy(false);
      }
    },
  };
}

function Edition({ edition }: { edition: NewsEdition }) {
  const editionSwitch = useEditionSwitch();
  const { lead, reader } = edition;
  const chips = [reader.place, reader.work, ...reader.topics].filter(
    (chip): chip is string => !!chip,
  );
  return (
    <>
      <section className="pgs-section" aria-label="Notícias, edição completa de hoje">
        <EditionHead />
        <div className="pgs-note">
          <i aria-hidden="true">
            <PaperIcon />
          </i>
          <span>
            <b>{edition.opening || 'Sua edição de hoje'}</b>
            <small>
              {editionDay(edition.generatedAt)}. Escrita com IA
              {chips.length > 0 ? `, feita para ${chips.join(', ')}.` : '.'}
            </small>
          </span>
        </div>
        <Story story={lead} lead />
      </section>

      {edition.sections.map((section) => (
        <section key={section.key} className="pgs-section">
          <h2 className="pgs-h2">
            {section.title} <small>{section.stories.length}</small>
          </h2>
          {section.stories.map((story) => (
            <Story key={story.id} story={story} />
          ))}
        </section>
      ))}

      {edition.quick.length > 0 && (
        <section className="pgs-section">
          <h2 className="pgs-h2">
            Em uma linha <small>{edition.quick.length}</small>
          </h2>
          {edition.quick.map((item) => (
            <a
              key={item.source.url}
              className="pgs-row ne-row"
              href={item.source.url}
              target="_blank"
              rel="noreferrer"
            >
              <span className="pgs-rowt">
                <span className="ne-quick">{item.text}</span>
                <small>{item.source.name}</small>
              </span>
              <span className="ne-go" aria-hidden="true">
                ↗
              </span>
            </a>
          ))}
        </section>
      )}

      {(!reader.place || !reader.work) && (
        <section className="pgs-section">
          <h2 className="pgs-h2">Quer a edição ainda mais sua?</h2>
          {!reader.place && (
            <Link href="/perfil" className="pgs-row ne-row">
              <span className="pgs-rowt">
                <b>Me conta sua cidade</b>
                <small>No Perfil. Eu trago o que acontece aí.</small>
              </span>
              <span className="ne-go" aria-hidden="true">
                ›
              </span>
            </Link>
          )}
          {!reader.work && (
            <Link href="/sobre-voce" className="pgs-row ne-row">
              <span className="pgs-rowt">
                <b>Me conta com o que você trabalha</b>
                <small>Em Sobre você. Eu acompanho o seu setor.</small>
              </span>
              <span className="ne-go" aria-hidden="true">
                ›
              </span>
            </Link>
          )}
        </section>
      )}

      <p className="ne-fine">
        Escrita com IA a partir de notícias públicas. A reportagem completa fica no veículo.
      </p>
      <div className="pgs-actions">
        <button
          type="button"
          className="pgs-btn small"
          disabled={editionSwitch.busy}
          onClick={() => void editionSwitch.set(false)}
        >
          Desligar a edição completa
        </button>
      </div>
    </>
  );
}

function Writing() {
  return (
    <section className="pgs-section" aria-live="polite">
      <EditionHead />
      <div className="pgs-note">
        <i aria-hidden="true">
          <PaperIcon />
        </i>
        <span>
          <b>Escrevendo a sua edição</b>
          <small>Leio as fontes do dia e separo o que importa pra você.</small>
        </span>
      </div>
      <Skeleton variant="rows" label="Escrevendo a sua edição" />
    </section>
  );
}

/** Para quem ainda não tem o Completo: mostra o que a edição completa entrega. */
function Teaser() {
  return (
    <section className="pgs-section">
      <EditionHead />
      <div className="pgs-note">
        <i aria-hidden="true">
          <PaperIcon />
        </i>
        <span>
          <b>Sua edição completa, escrita pra você</b>
          <small>
            O que acontece na sua cidade, no seu trabalho e nos assuntos de que você mais gosta,
            explicado com calma e com o porquê de importar pra você.
          </small>
        </span>
      </div>
      <article className="ne-story ne-sample" aria-hidden="true">
        <small className="ne-topic">Exemplo · No seu trabalho</small>
        <h3 className="ne-title">Regra nova muda a venda de clareadores</h3>
        <div className="ne-text open">
          <p>
            A agência de vigilância sanitária mudou o que pode ser vendido sem receita. Consultórios
            têm um prazo para se adaptar.
          </p>
        </div>
        <div className="ne-why">
          <b>Por que importa pra você</b>
          <p>Muda o que o seu consultório pode indicar aos pacientes a partir do mês que vem.</p>
        </div>
      </article>
      <div className="pgs-actions">
        <button
          type="button"
          className="pgs-btn solid"
          onClick={() => openPaywall('news', 'noticias_edicao')}
        >
          Quero a edição completa
        </button>
      </div>
    </section>
  );
}

function Ask({ kind }: { kind: 'consent' | 'optin' }) {
  const editionSwitch = useEditionSwitch();
  return (
    <section className="pgs-section">
      <EditionHead />
      <div className="pgs-note">
        <i aria-hidden="true">
          <PaperIcon />
        </i>
        <span>
          <b>Sua edição, escrita pra você todo dia</b>
          <small>Uso sua cidade, seu trabalho e seus assuntos, nunca seu nome ou telefone.</small>
        </span>
      </div>
      <div className="pgs-actions">
        {kind === 'consent' ? (
          <button
            type="button"
            className="pgs-btn solid"
            onClick={() => window.dispatchEvent(new Event(AI_CONSENT_EVENT))}
          >
            Ver a permissão de inteligência artificial
          </button>
        ) : (
          <button
            type="button"
            className="pgs-btn solid"
            disabled={editionSwitch.busy}
            onClick={() => void editionSwitch.set(true)}
          >
            {editionSwitch.busy ? 'Ligando…' : 'Montar minha edição'}
          </button>
        )}
      </div>
    </section>
  );
}

export function NewsProEdition({ pro }: { pro: boolean }) {
  const editionQ = useQuery({
    queryKey: NEWS_EDITION_KEY,
    queryFn: getNewsEdition,
    enabled: pro,
    staleTime: 5 * 60_000,
    refetchInterval: (query) => (query.state.data?.status === 'generating' ? 4000 : false),
  });
  if (!pro) return <Teaser />;
  const state = editionQ.data;
  if (!state || state.status === 'generating') return <Writing />;
  if (state.status === 'locked') return <Teaser />;
  if (state.status === 'consent' || state.status === 'optin') return <Ask kind={state.status} />;
  if (state.status === 'empty') {
    return (
      <section className="pgs-section">
        <EditionHead />
        <div className="pgs-note">
          <i aria-hidden="true">!</i>
          <span>
            <b>A edição completa não saiu agora.</b>
            <small>
              {state.reason === 'no_news'
                ? 'As fontes ainda não publicaram o bastante hoje.'
                : 'Tento de novo em alguns minutos.'}
            </small>
          </span>
        </div>
        <div className="pgs-actions">
          <button type="button" className="pgs-btn small" onClick={() => void editionQ.refetch()}>
            Ver de novo
          </button>
        </div>
      </section>
    );
  }
  return <Edition edition={state.edition} />;
}
